import App from '@components/App';
import { ApplicantCalender } from '@pages/ApplicantCalender/ApplicantCalender';
import BlockAccounts from '@pages/BlockAccounts/BlockAccounts';
import EmployerDashboard from '@pages/EmployerDashboard/EmployerDashboard';
import EnterpriseBilling from '@pages/EnterpriseBilling/EnterpriseBilling';
import { EnterpriseCalender } from '@pages/EnterpriseCalender/EnterpriseCalender';
import EnterPriseCompanies from '@pages/EnterPriseCompanies/EnterPriseCompanies';
import EnterPriseDashboard from '@pages/EnterPriseDashboard/EnterPriseDashboard';
import EnterpriseOnboarding from '@pages/EnterpriseOnboarding/EnterpriseOnboarding';
import EnterpriseTemplates from '@pages/EnterpriseTemplates/EnterpriseTemplates';
import NotFoundPage from '@pages/NotFoundPage/NotFoundPage';
import RockerboxCompany from '@pages/RockerboxCompany/RockerboxCompany';
import RockerboxDashboard from '@pages/RockerboxDashboard/RockerboxDashboard';
import SpamAccounts from '@pages/SpamAccounts/SpamAccounts';
import TampleteDetail from '@pages/TampleteDetail/TampleteDetail';
import { createBrowserRouter, RouteObject } from 'react-router-dom';

import { ProtectedRoute } from './ProtectedRoute';

// This is needed to redirect from React pages to Angular pages
const ReloadPageComponent = () => {
  window.location.reload();
  return null;
};

function ProtectedLazy(
  importFunc: () => Promise<{ default: React.ComponentType<any> }>,
  requiredRole?: string,
): RouteObject['lazy'] {
  return async () => {
    const module = (await importFunc()) as {
      default: React.ComponentType<any>;
    };
    return {
      Component: (props: any) => (
        <ProtectedRoute
          element={<module.default {...props} />}
          requiredRole={requiredRole}
        />
      ),
    };
  };
}

// TODO: add different routers for different user types
export const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      // employer routes
      {
        path: '/dashboard/',
        element: (
          <ProtectedRoute
            element={<EmployerDashboard />}
            requiredRole="company"
          />
        ),
      },
      {
        path: '/inbox/',
        lazy: ProtectedLazy(
          () => import('@features/messaging/components/InboxPage/InboxPage'),
          'company',
        ),
      },
      {
        path: '/:companySlug/',
        lazy: ProtectedLazy(
          () => import('@pages/Company/Company'),
          'applicant',
        ),
      },
      {
        path: '/employer/calendar/',
        lazy: ProtectedLazy(
          () => import('@pages/Calendar/Calendar'),
          'company',
        ),
      },
      {
        path: '/employer/positions/',
        lazy: ProtectedLazy(
          () => import('@pages/EmployerPositions/EmployerPositions'),
          'company',
        ),
      },
      {
        path: '/employer/application-questions/',
        lazy: ProtectedLazy(
          () => import('@pages/ApplicationQuestions/ApplicationQuestions'),
          'company',
        ),
      },
      {
        path: '/employer/integrations/',
        lazy: ProtectedLazy(
          () => import('@pages/Integrations/Integrations'),
          'company',
        ),
      },
      {
        path: '/employer/my-team/',
        lazy: ProtectedLazy(() => import('@pages/MyTeam/MyTeam'), 'company'),
      },
      {
        path: '/employer/account/onboarding/',
        lazy: ProtectedLazy(
          () =>
            import(
              '@pages/EmployerDigitalOnboarding/EmployerDigitalOnboarding'
            ),
          'company',
        ),
      },
      {
        path: '/employer/account/',
        lazy: ProtectedLazy(
          () => import('@pages/EmployerAccount/EmployerAccount'),
          'company',
        ),
      },
      {
        path: '/employer/hireflix-interviews/',
        lazy: ProtectedLazy(
          () => import('@pages/HireflixIntegration/HireflixIntegration'),
          'company',
        ),
      },

      // enterprise routes
      {
        path: '/enterprise/',
        element: (
          <ProtectedRoute
            element={<EnterPriseDashboard />}
            requiredRole="enterprise"
          />
        ),
      },
      {
        path: '/enterprise/account/',
        lazy: ProtectedLazy(
          () => import('@pages/EnterPriseAccount/EnterPriseAccount'),
          'enterprise',
        ),
      },
      {
        path: '/enterprise/companies/',
        element: (
          <ProtectedRoute
            element={<EnterPriseCompanies />}
            requiredRole="enterprise"
          />
        ),
      },
      {
        path: '/enterprise/onboarding/',
        element: (
          <ProtectedRoute
            element={<EnterpriseOnboarding />}
            requiredRole="enterprise"
          />
        ),
      },
      {
        path: '/enterprise/inbox/',
        lazy: ProtectedLazy(
          () =>
            import(
              '@features/messaging/components/EnterpriseInboxPage/EnterpriseInboxPage'
            ),
          'enterprise',
        ),
      },
      {
        path: '/enterprise/calendar/',
        element: (
          <ProtectedRoute
            element={<EnterpriseCalender />}
            requiredRole="enterprise"
          />
        ),
      },
      {
        path: '/enterprise/templates/',
        element: (
          <ProtectedRoute
            element={<EnterpriseTemplates />}
            requiredRole="enterprise"
          />
        ),
      },
      {
        path: '/enterprise/template/:id/',
        element: (
          <ProtectedRoute
            element={<TampleteDetail />}
            requiredRole="enterprise"
          />
        ),
      },
      {
        path: '/enterprise/billing/',
        element: (
          <ProtectedRoute
            element={<EnterpriseBilling />}
            requiredRole="enterprise"
          />
        ),
      },

      // rockerbox routes
      {
        path: '/rockerbox/',
        element: (
          <ProtectedRoute
            element={<RockerboxDashboard />}
            requiredRole="rockerbox"
          />
        ),
      },
      {
        path: '/rockerbox/company/:id/',
        element: (
          <ProtectedRoute
            element={<RockerboxCompany />}
            requiredRole="rockerbox"
          />
        ),
      },
      {
        path: '/rockerbox/messages/',
        lazy: ProtectedLazy(
          () => import('@features/messaging/components/InboxPage/InboxPage'),
          'rockerbox',
        ),
      },

      // admin routes
      {
        path: '/admin/',
        lazy: ProtectedLazy(
          () => import('@pages/AdminEmployers/AdminEmployers'),
          'admin',
        ),
      },

      {
        path: '/admin/enterprises/',
        lazy: ProtectedLazy(
          () => import('@pages/AdminEnterprises/AdminEnterprises'),
          'admin',
        ),
      },
      {
        path: '/admin/applicants/',
        lazy: ProtectedLazy(
          () => import('@pages/AdminApplicants/AdminApplicants'),
          'admin',
        ),
      },
      {
        path: '/admin/templates/',
        lazy: ProtectedLazy(
          () => import('@pages/AdminTemplates/AdminTemplates'),
          'admin',
        ),
      },
      // {
      //   path: '/admin/spam-companies/',
      //   element: (
      //     <ProtectedRoute element={<SpamAccounts />} requiredRole="admin" />
      //   ),
      // },
      {
        path: '/admin/block-companies/',
        element: (
          <ProtectedRoute element={<BlockAccounts />} requiredRole="admin" />
        ),
      },
      {
        path: '/admin/employers/',
        lazy: ProtectedLazy(
          () => import('@pages/AdminEmployers/AdminEmployers'),
          'admin',
        ),
      },
      {
        path: '/admin/industries-positions/',
        lazy: ProtectedLazy(
          () =>
            import(
              '@pages/AdminPositionAndIndustries/AdminPositionAndIndustries'
            ),
          'admin',
        ),
      },
      {
        path: '/admin/manage-users/',
        lazy: ProtectedLazy(
          () => import('@pages/AdminManageUsers/AdminManageUsers'),
          'admin',
        ),
      },
      {
        path: '/admin/wotc-users/',
        lazy: ProtectedLazy(
          () => import('@pages/WotcsUsers/WotcsUsers'),
          'admin',
        ),
      },
      {
        path: '/admin/crm-tagging/',
        lazy: ProtectedLazy(
          () => import('@pages/CRMTagging/CRMTagging'),
          'admin',
        ),
      },
      {
        path: '/references/thankyou/',
        lazy: ProtectedLazy(
          () =>
            import('@pages/EmployerReferenceLanding/EmployerReferenceLanding'),
          'admin',
        ),
      },

      // aplicants
      {
        path: '/applicants/',
        lazy: ProtectedLazy(
          () => import('@pages/ApplicantDashboard/ApplicantDashboard'),
          'applicant',
        ),
      },
      {
        path: '/applicants/inbox/',
        lazy: ProtectedLazy(
          () => import('@features/messaging/components/InboxPage/InboxPage'),
          'applicant',
        ),
      },
      {
        path: '/applicants/calendar/',
        element: (
          <ProtectedRoute
            element={<ApplicantCalender />}
            requiredRole="applicant"
          />
        ),
      },
      {
        path: '/applicants/job-interests/',
        lazy: ProtectedLazy(
          () => import('@pages/JobInterests/JobInterests'),
          'applicant',
        ),
      },
      {
        path: '/applicants/resume/',
        lazy: ProtectedLazy(
          () => import('@pages/ApplicantResume/ApplicantResume'),
          'applicant',
        ),
      },
      {
        path: '/applicants/onboarding/',
        lazy: ProtectedLazy(
          () => import('@pages/ApplicantOnboarding/ApplicantOnboarding'),
          'applicant',
        ),
      },
      {
        path: '/applicants/account/',
        lazy: ProtectedLazy(
          () => import('@pages/ApplicantAccount/ApplicantAccount'),
          'applicant',
        ),
      },
      // no routes found
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
